import React, { useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { 
  LayoutDashboard, 
  FolderOpen, 
  Users, 
  Wallet, 
  Upload, 
  LogOut, 
  RefreshCw,
  FileText
} from 'lucide-react';
import NotificationModal from './NotificationModal';
import './Layout.css';

const SyncStrapiButton = () => {
  const [syncing, setSyncing] = useState(false);
  const [lastSync, setLastSync] = useState(null);
  const [notification, setNotification] = useState({ show: false, type: 'success', title: '', message: '' });

  const handleSync = async () => {
    setSyncing(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/sync/strapi-pagos`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const result = await response.json();
        setLastSync(new Date().toLocaleTimeString('es-VE'));
        console.log('✅ Sincronización completada:', result);
        setNotification({
          show: true,
          type: 'success',
          title: 'Sincronización Completada',
          message: `${result.expedientes_actualizados} expedientes actualizados exitosamente`
        });
      } else {
        throw new Error('Error en sincronización');
      }
    } catch (error) {
      console.error('Error:', error);
      setNotification({
        show: true,
        type: 'error',
        title: 'Error de Sincronización',
        message: 'No se pudo completar la sincronización con Strapi'
      });
    } finally {
      setSyncing(false);
    }
  };

  return (
    <>
      <button 
        onClick={handleSync} 
        disabled={syncing}
        className="sync-strapi-btn" 
        data-testid="sync-strapi-button"
        style={{
          width: '100%',
          padding: '10px 16px',
          background: syncing ? 'rgba(107, 114, 128, 0.3)' : 'rgba(251, 176, 64, 0.2)',
          border: '1px solid rgba(251, 176, 64, 0.4)',
          borderRadius: '8px',
          color: syncing ? '#9ca3af' : '#fbb040',
          cursor: syncing ? 'not-allowed' : 'pointer',
          fontSize: '14px',
          fontWeight: '500',
          transition: 'all 0.2s',
          marginBottom: '12px',
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          justifyContent: 'center'
        }}
        title="Sincronizar pagos desde Strapi manualmente"
      >
        <RefreshCw size={16} className={syncing ? 'spinning' : ''} />
        <span>{syncing ? 'Sincronizando...' : 'Sync Strapi'}</span>
        {lastSync && <small style={{ display: 'block', fontSize: '12px', opacity: 0.7 }}>
          Última: {lastSync}
        </small>}
      </button>
      
      <NotificationModal
        isOpen={notification.show}
        type={notification.type}
        title={notification.title}
        message={notification.message}
        onClose={() => setNotification({ show: false, type: 'success', title: '', message: '' })}
      />
    </>
  );
};

const Layout = ({ children }) => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(true);

  const navigation = [
    { name: 'Dashboard', path: '/', icon: <LayoutDashboard size={20} />, roles: ['admin', 'auditor', 'consulta'] },
    { name: 'Expedientes', path: '/expedientes', icon: <FolderOpen size={20} />, roles: ['admin', 'auditor', 'consulta'] },
    { name: 'Usuarios', path: '/usuarios', icon: <Users size={20} />, roles: ['admin'] },
    { name: 'Pagos de Oficina', path: '/pagos-oficina', icon: <Wallet size={20} />, roles: ['admin', 'auditor'] },
    { name: 'Reportes', path: '/reportes', icon: <FileText size={20} />, roles: ['admin', 'auditor'] },
    { name: 'Importar', path: '/importacion', icon: <Upload size={20} />, roles: ['admin'] },
  ];

  const filteredNavigation = navigation.filter(item => item.roles.includes(user?.role));

  const getRoleBadge = (role) => {
    const badges = {
      admin: { label: 'Admin', color: '#a855f7' },
      auditor: { label: 'Auditor', color: '#3b82f6' },
      consulta: { label: 'Consulta', color: '#6b7280' },
    };
    return badges[role] || badges.consulta;
  };

  const roleBadge = getRoleBadge(user?.role);

  return (
    <div className="layout-container" data-testid="layout">
      <aside className={`sidebar ${sidebarOpen ? 'sidebar-open' : 'sidebar-closed'}`} data-testid="sidebar">
        {/* Logo */}
        <div className="sidebar-header">
          {sidebarOpen ? (
            <div className="logo-container">
              <img src="/kompa-logo.png" alt="Kompa" className="kompa-logo" />
              <div className="logo-text">
                <p>Auditor Inteligente</p>
              </div>
            </div>
          ) : (
            <img src="/kompa-logo.png" alt="Kompa" className="kompa-logo-small" />
          )}
        </div>

        {/* Navigation */}
        <nav className="sidebar-nav" data-testid="navigation">
          {filteredNavigation.map((item) => {
            const isActive = location.pathname === item.path;
            return (
              <Link
                key={item.path}
                to={item.path}
                className={`nav-item ${isActive ? 'nav-item-active' : ''}`}
                data-testid={`nav-${item.name.toLowerCase().replace(' ', '-')}`}
              >
                <span className="nav-icon">{item.icon}</span>
                {sidebarOpen && <span className="nav-label">{item.name}</span>}
              </Link>
            );
          })}
        </nav>

        {/* User Section */}
        <div className="sidebar-footer">
          {sidebarOpen ? (
            <div className="user-section">
              <div className="user-info">
                <div className="user-avatar">
                  {user?.name?.charAt(0).toUpperCase()}
                </div>
                <div className="user-details">
                  <p className="user-name">{user?.name}</p>
                  <span className="user-role" style={{ backgroundColor: `${roleBadge.color}20`, color: roleBadge.color, borderColor: `${roleBadge.color}40` }}>
                    {roleBadge.label}
                  </span>
                </div>
              </div>
              
              {/* Botón Sincronización Strapi - Solo Admin */}
              {user?.role === 'admin' && (
                <SyncStrapiButton />
              )}
              
              <button onClick={logout} className="logout-btn" data-testid="logout-button">
                <LogOut size={16} />
                <span>Cerrar Sesión</span>
              </button>
            </div>
          ) : (
            <div className="user-section-collapsed">
              <div className="user-avatar-small">
                {user?.name?.charAt(0).toUpperCase()}
              </div>
              <button onClick={logout} className="logout-btn-small" data-testid="logout-button-collapsed" title="Cerrar Sesión">
                <LogOut size={18} />
              </button>
            </div>
          )}
        </div>

        {/* Toggle */}
        <button onClick={() => setSidebarOpen(!sidebarOpen)} className="sidebar-toggle" data-testid="sidebar-toggle">
          <svg className={`toggle-icon ${sidebarOpen ? '' : 'toggle-icon-rotated'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
        </button>
      </aside>

      <main className="main-content" data-testid="main-content">
        {children}
      </main>
    </div>
  );
};

export default Layout;